import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  IconButton,
  Button,
  CircularProgress,
  Stack,
  Grid,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigation } from "contexts/notificationsContext";
import { DashboardStackCustom, StackCustom } from "components/StackCustom/styles";
import { IAcabamento, ISelectOption } from "./types/acabamento.types";
import {
  obterAcabamentoPorId,
  salvarAcabamento,
  excluirAcabamento,
  duplicarAcabamento,
  listarSituacoes,
  listarStatus,
  listarUnidades,
  listarTiposAcabamento,
  listarReferencias,
  obterHistoricoAcabamento,
  validarCodigoUnicoAcabamento,
} from "./services/acabamentoService";
import { useValidacaoCodigoUnico } from "../hooks/useValidacaoCodigoUnico";
import { useSomenteLeituraN1 } from "../hooks/useSomenteLeituraN1";
import { AvisoPlanilhaPendenteN1 } from "../components/AvisoPlanilhaPendenteN1";
import { useFormularioAlterado } from "../hooks/useFormularioAlterado";
import { FormularioAcabamento } from "./components/FormularioAcabamento";
import { ModalGerenciamentoTextura } from "./components/ModalGerenciamentoTextura";
import { ModalConfirmacaoExcluir } from "./components/ModalConfirmacaoExcluir";
import { ModalHistorico } from "./components/ModalHistorico";

// FASE 05 — S3/PO 2026-05-13: IdTipoTela do Acabamento (enum TipoTela = 5).
// Passado em listarReferencias para filtrar pelas referencias configuradas em
// Cadastro N1 da empresa. Acabamento nao tem cascata Linha/Prenativo.
const ID_TIPO_TELA_ACABAMENTO = 5;

// Helper para formatar data de atualização
const formatarDataAtualizacao = (data?: string) => {
  if (!data) return "";
  try {
    const date = new Date(data);
    return `${date.toLocaleDateString("pt-BR")} - ${date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
  } catch (e) {
    console.error("Erro ao formatar data:", e);
    return "";
  }
};

export const CadastroAcabamento: React.FC = () => {
  const navigate = useNavigate();
  const { id: idAcabamentoParam } = useParams<{ id: string }>();
  const idAcabamento = idAcabamentoParam ? parseInt(idAcabamentoParam) : undefined;
  const isEdicao = !!idAcabamento;

  const { openNotification } = useNavigation();

  // FASE 05 — 2ª validação/PO 2026-07-24 (B4): empresa "apenas consulta" = somente-leitura.
  // FASE 05 — 6ª validação/PO 2026-08-11: bloqueia edição também quando o MODO EDIÇÃO está desligado.
  // Plano 14/10/2026 (Frente 4): planilha de acabamentos em andamento também deixa a tela somente-leitura.
  const somenteLeitura = useSomenteLeituraN1("acabamentos");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // FASE 05 — Ajustes 25/08 (item 4): SALVAR só habilita depois de alguma alteração.
  // Em cadastro novo não há estado inicial a comparar — o hook devolve sempre habilitado.
  const [acabamento, setAcabamento] = useState<Partial<IAcabamento>>({
    codigo: "",
    codigoInterno: "",
    descricao: "",
    referencia: "",
    loteMinimo: 0,
    idUnidade: undefined,
    idTipoTratamento: undefined,
    idSituacao: undefined, // Será definido quando as listas carregarem
    idStatus: undefined, // Será definido quando as listas carregarem
  });

  // Listas de referência
  const [situacoes, setSituacoes] = useState<ISelectOption[]>([]);
  const [status, setStatus] = useState<ISelectOption[]>([]);
  const [unidades, setUnidades] = useState<ISelectOption[]>([]);
  const [tiposAcabamento, setTiposAcabamento] = useState<ISelectOption[]>([]);
  const [referencias, setReferencias] = useState<ISelectOption[]>([]);

  // FASE 05 — S3+/PO 2026-05-13: validacao em tempo real de codigo duplicado.
  const { codigoDuplicado } = useValidacaoCodigoUnico(
    acabamento.codigo,
    idAcabamento,
    validarCodigoUnicoAcabamento
  );

  // Modais
  const [modalTexturaAberto, setModalTexturaAberto] = useState(false);
  const [modalExcluirAberto, setModalExcluirAberto] = useState(false);
  const [modalHistoricoAberto, setModalHistoricoAberto] = useState(false);

  // Carregar listas de referência
  const carregarListasReferencia = useCallback(async () => {
    try {
      const results = await Promise.allSettled([
        listarSituacoes(),
        listarStatus(),
        listarUnidades(),
        listarTiposAcabamento(),
        listarReferencias(ID_TIPO_TELA_ACABAMENTO),
      ]);

      const [
        situacoesResult,
        statusResult,
        unidadesResult,
        tiposResult,
        refsResult,
      ] = results;

      const situacoesData = situacoesResult.status === 'fulfilled' ? situacoesResult.value : [];
      const statusData = statusResult.status === 'fulfilled' ? statusResult.value : [];
      const unidadesData = unidadesResult.status === 'fulfilled' ? unidadesResult.value : [];
      const tiposData = tiposResult.status === 'fulfilled' ? tiposResult.value : [];
      const refsData = refsResult.status === 'fulfilled' ? refsResult.value : [];

      setSituacoes(situacoesData);
      setStatus(statusData);
      setUnidades(unidadesData);
      setTiposAcabamento(tiposData);
      setReferencias(refsData);
      
      // Aplicar valores padrão apenas quando as listas estiverem carregadas
      setAcabamento(prev => {
        const updated = { ...prev };
        // Só definir valores padrão se as listas tiverem itens e o valor não estiver definido
        if (situacoesData.length > 0 && !updated.idSituacao) {
          updated.idSituacao = situacoesData.find(s => s.id === 1)?.id || situacoesData[0]?.id;
        }
        if (statusData.length > 0 && !updated.idStatus) {
          updated.idStatus = statusData.find(s => s.id === 1)?.id || statusData[0]?.id;
        }
        return updated;
      });
    } catch (error) {
      console.error("Erro ao carregar listas de referência:", error);
      // Não mostrar erro, apenas logar - Promise.allSettled já trata os erros individuais
    }
  }, []);

  // Carregar dados do acabamento (se edição)
  const carregarAcabamento = useCallback(async () => {
    if (!idAcabamento) {
      setLoading(false);
      return;
    }

    try {
      const acabamentoData = await obterAcabamentoPorId(idAcabamento);
      
      if (acabamentoData) {
        setAcabamento(acabamentoData);
      } else {
        openNotification({ message: "Acabamento não encontrado", type: "error" });
        navigate("/acabamentosN1");
      }
    } catch (error) {
      console.error("Erro ao carregar acabamento:", error);
      openNotification({ message: "Erro ao carregar acabamento", type: "error" });
    } finally {
      setLoading(false);
    }
  }, [idAcabamento, navigate, openNotification]);

  useEffect(() => {
    carregarListasReferencia();
  }, [carregarListasReferencia]);

  useEffect(() => {
    if (situacoes.length > 0 || !idAcabamento) {
      carregarAcabamento();
    }
  }, [situacoes, carregarAcabamento]);

  const handleAcabamentoChange = (updates: Partial<IAcabamento>) => {
    setAcabamento((prev) => ({ ...prev, ...updates }));
  };

  const { alterado, marcarComoSalvo } = useFormularioAlterado(
    acabamento,
    { ehNovo: !isEdicao, pronto: !loading }
  );

  const handleSalvar = async () => {
    // Validações básicas
    if (!acabamento.codigo || !acabamento.descricao || !acabamento.idTipoTratamento) {
      openNotification({
        message: "Preencha os campos obrigatórios para salvar.",
        type: "warning",
      });
      return;
    }

    // FASE 05 — 5ª validação/Frente 2 2026-07-30: Referência obrigatória (decisão do PO de 30/07).
    if (!acabamento.idReferencia) {
      openNotification({
        message: "Selecione uma referência para salvar.",
        type: "warning",
      });
      return;
    }

    // FASE 05 — S3+/PO 2026-05-13: bloquear save se codigo ja cadastrado.
    if (codigoDuplicado) {
      openNotification({
        message: "Código já cadastrado para outro acabamento. Escolha um código diferente.",
        type: "warning",
      });
      return;
    }

    if (!acabamento.idUnidade || acabamento.idUnidade <= 0) {
      openNotification({
        message: "Selecione uma unidade para salvar.",
        type: "warning",
      });
      return;
    }

    if (acabamento.loteMinimo === undefined || acabamento.loteMinimo < 0 || acabamento.loteMinimo > 99999) {
      openNotification({
        message: "Lote mínimo deve ser um número inteiro entre 0 e 99999.",
        type: "warning",
      });
      return;
    }

    setSaving(true);
    try {
      const dadosParaSalvar: Partial<IAcabamento> = {
        ...acabamento,
      };

      const resultado = await salvarAcabamento(dadosParaSalvar, unidades);

      if (resultado?.sucesso) {
        // Ajustes 25/08 (item 4): salvou — SALVAR volta a ficar desabilitado até a próxima alteração.
        marcarComoSalvo();
        openNotification({
          message: "Acabamento salvo com sucesso",
          type: "success",
        });
        
        if (!isEdicao && resultado.id) {
          navigate(`/produtosN1/acabamentos/${resultado.id}`);
        } else if (isEdicao && resultado.id && String(resultado.id) !== String(idAcabamento)) {
          // FASE 05 — 6ª validação/PO 2026-08-11 (F3 Etapa 2): o salvar congelou a versão de
          // mercado e criou uma NOVA cópia de trabalho — navegar para ela.
          navigate(`/produtosN1/acabamentos/${resultado.id}`, { replace: true });
        }
      } else {
        openNotification({
          message: resultado?.mensagem || "Erro ao salvar acabamento",
          type: "error",
        });
      }
    } catch (error: any) {
      // FASE 05 — 6ª validação/PO 2026-08-04: exibir a mensagem real da API (getErrorsByApi lança).
      console.error("Erro ao salvar acabamento:", error);
      openNotification({
        message: error?.message || "Erro ao salvar acabamento. Tente novamente.",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicar = async () => {
    if (!idAcabamento) return;

    try {
      const resultado = await duplicarAcabamento(idAcabamento);
      if (resultado?.sucesso && resultado.id) {
        openNotification({
          message: "Cópia criada. Revise os dados antes de salvar.",
          type: "success",
        });
        navigate(`/produtosN1/acabamentos/${resultado.id}`);
      } else {
        openNotification({
          message: resultado?.mensagem || "Erro ao duplicar acabamento",
          type: "error",
        });
      }
    } catch (error) {
      console.error("Erro ao duplicar acabamento:", error);
      openNotification({
        message: "Erro ao duplicar acabamento",
        type: "error",
      });
    }
  };

  const handleExcluir = async () => {
    if (!idAcabamento) return;

    try {
      const sucesso = await excluirAcabamento(idAcabamento);
      if (sucesso) {
        openNotification({
          message: "Acabamento excluído com sucesso",
          type: "success",
        });
        navigate("/acabamentosN1");
      } else {
        openNotification({
          message: "Erro ao excluir acabamento",
          type: "error",
        });
      }
    } catch (error) {
      console.error("Erro ao excluir acabamento:", error);
      openNotification({
        message: "Erro ao excluir acabamento",
        type: "error",
      });
    } finally {
      setModalExcluirAberto(false);
    }
  };

  const handleTexturaSave = (updates: Partial<IAcabamento>, idAlvo?: number) => {
    setAcabamento((prev) => ({ ...prev, ...updates }));
    // Plano 21/09/2026 (Frente 3): acabamento Disponível → a textura/cor foi para a cópia de
    // trabalho; navegar para ela (mesmo tratamento do handleSalvar quando o espelho cria a cópia).
    if (idAlvo && String(idAlvo) !== String(idAcabamento)) {
      navigate(`/produtosN1/acabamentos/${idAlvo}`, { replace: true });
    }
  };

  const handleRefreshAcabamento = async () => {
    if (idAcabamento) {
      const acabamentoData = await obterAcabamentoPorId(idAcabamento);
      if (acabamentoData) {
        // FASE 05 — 6ª validação/PO 2026-08-06: preservar a textura recém-gravada se o refetch vier
        // sem ela (o GET pós-upload às vezes voltava sem a figura e a imagem "sumia" mesmo com 200).
        setAcabamento((prev) => ({
          ...acabamentoData,
          texturaFiguraUrl: acabamentoData.texturaFiguraUrl || prev.texturaFiguraUrl,
          texturaFiguraNome: acabamentoData.texturaFiguraNome || prev.texturaFiguraNome,
        }));
      }
    }
  };

  // Plano 30/08/2026, Frente 1: o destino era fixo em "/produtosN1", então sair da edição de
  // um acabamento jogava o usuário na página de Produtos. Desde a decisão F05A-P1 (26/08)
  // Acabamentos tem página própria e saiu do seletor de Produtos (que ficou com 5 tipos), ou
  // seja, "/acabamentosN1" é a única porta de entrada — não há origem ambígua a rastrear.
  const handleVoltar = () => {
    navigate("/acabamentosN1");
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <DashboardStackCustom sx={{ width: "100%" }} pt={2}>
      <Grid container display={"flex"} alignItems={"center"}>
        {/* Header: Título + Breadcrumb - Fora do StackCustom para ter cor de fundo da página */}
        <Stack direction="row" alignItems="center" spacing={1} mb={0.5} sx={{ width: "100%", px: 4.5 }}>
          <IconButton onClick={handleVoltar} size="small" sx={{ color: "#666" }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" sx={{ color: "#333", fontWeight: 400, fontSize: "1.8rem" }}>
            Cadastro de Acabamento
          </Typography>
        </Stack>
        
        {/* Breadcrumb abaixo do título */}
        <Box sx={{ 
          borderBottom: "2px solid #3C90AD", 
          pb: 1.5, 
          mb: 2,
          ml: 4.5,
          width: "100%",
        }}>
          <Breadcrumbs separator="›" sx={{ mt: 0 }}>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={(e) => { e.preventDefault(); navigate("/"); }}
              sx={{ fontSize: "0.75rem", textDecoration: "none", cursor: "pointer" }}
            >
              Home
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={(e) => { e.preventDefault(); navigate("/acabamentosN1"); }}
              sx={{ fontSize: "0.75rem", textDecoration: "none", cursor: "pointer" }}
            >
              Cadastros
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={(e) => { e.preventDefault(); navigate("/acabamentosN1"); }}
              sx={{ fontSize: "0.75rem", textDecoration: "none", cursor: "pointer" }}
            >
              Acabamentos
            </Link>
            <Typography color="text.primary" sx={{ fontSize: "0.75rem" }}>
              {isEdicao ? "Editar" : "Novo Acabamento"}
            </Typography>
          </Breadcrumbs>
        </Box>

        <StackCustom>
          
          {/* Última atualização e Log */}
          {/* Reporte 02/09 (A8): so isEdicao, como no Componente — exigir dataUltimaAtualizacao escondia o link em registro nunca atualizado */}
          {isEdicao && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 2, gap: 2 }}>
              {formatarDataAtualizacao(acabamento.dataUltimaAtualizacao) && (
                <Typography variant="body2" color="text.secondary">
                  Última atualização: {formatarDataAtualizacao(acabamento.dataUltimaAtualizacao)}
                </Typography>
              )}
              <Link
                component="button"
                variant="body2"
                onClick={() => setModalHistoricoAberto(true)}
                sx={{ color: "#3C90AD" }}
              >
                Log de atualizações
              </Link>
            </Box>
          )}

          <AvisoPlanilhaPendenteN1 tipoMaterial="acabamentos" />

          {/* FASE 05 — 6ª validação/PO 2026-08-13: layout no padrão dos demais cadastros N1 —
              Situação sozinha na linha de cima e, abaixo, quadro de Cor/Textura à esquerda com
              os campos à direita. As duas colunas são montadas dentro do formulário. */}
          {/* FASE 05 — 6ª validação/PO 2026-08-11: somente-leitura bloqueia os campos. */}
          <Box sx={{ ...(somenteLeitura && { opacity: 0.7, pointerEvents: "none" }) }}>
            <FormularioAcabamento
              acabamento={acabamento}
              onChange={handleAcabamentoChange}
              situacoes={situacoes}
              status={status}
              unidades={unidades}
              tiposAcabamento={tiposAcabamento}
              referencias={referencias}
              codigoDuplicado={codigoDuplicado}
              onOpenModalTextura={() => setModalTexturaAberto(true)}
            />
          </Box>
        </StackCustom>

        {/* Rodapé com botões de ação — oculto em somente-leitura. */}
        {!somenteLeitura && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 2,
            mt: 3,
            width: "100%",
          }}
        >
          {isEdicao && (
            <>
              <Button
                variant="text"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setModalExcluirAberto(true)}
                disabled={saving}
              >
                EXCLUIR
              </Button>
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={handleDuplicar}
                disabled={saving}
                sx={{ borderColor: "#3C90AD", color: "#3C90AD" }}
              >
                DUPLICAR
              </Button>
            </>
          )}
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSalvar}
            disabled={saving || !alterado}
            sx={{ backgroundColor: "#3C90AD", "&:hover": { backgroundColor: "#2d6f87" } }}
          >
            SALVAR
          </Button>
        </Box>
        )}
      </Grid>

      {/* Modais */}
      <ModalGerenciamentoTextura
        open={modalTexturaAberto}
        onClose={() => setModalTexturaAberto(false)}
        acabamento={acabamento}
        onSave={handleTexturaSave}
        onRefreshAcabamento={handleRefreshAcabamento}
      />
      <ModalConfirmacaoExcluir
        open={modalExcluirAberto}
        onClose={() => setModalExcluirAberto(false)}
        onConfirm={handleExcluir}
        acabamentoCodigo={acabamento.codigo || ""}
        acabamentoDescricao={acabamento.descricao || ""}
      />
      <ModalHistorico
        open={modalHistoricoAberto}
        onClose={() => setModalHistoricoAberto(false)}
        idAcabamento={idAcabamento}
      />
    </DashboardStackCustom>
  );
};
